import { brandKitValidator } from "@redotech/redo-content-builder-common/brand-kit";
import {
  type FormElement,
  MarketingForgeElementType,
} from "@redotech/redo-content-builder-common/element-schema";
import type { SpotlightDocument } from "@redotech/redo-spotlight-common/spotlight-document-schema";
import {
  getSpotlightTriggerType,
  SpotlightDeviceVisibility,
  type SpotlightForm,
  SpotlightFormSchema,
  SpotlightGeoTargetingType,
  type SpotlightStep,
  SpotlightTriggerType,
} from "@redotech/redo-spotlight-common/spotlight-schema";
import * as z from "zod";

export type RcsOptInViewport = "desktop" | "mobile";

interface RcsOptInSpotlight {
  readonly spotlightId: string;
  /** The storefront refuses a forced spotlight on a device it does not appear on. */
  readonly viewport: RcsOptInViewport;
}

/** RCS registration is US-only, and a forced id still applies geo-targeting. */
const OPT_IN_COUNTRY = "US";

function elementsCollectPhone(elements: readonly FormElement[]): boolean {
  return elements.some((element) =>
    element.type === MarketingForgeElementType.COLUMN ||
    element.type === MarketingForgeElementType.CONTAINER
      ? elementsCollectPhone(element.elements)
      : element.type === MarketingForgeElementType.INPUT &&
        element.inputType === "phone",
  );
}

function stepCollectsPhone(step: SpotlightStep): boolean {
  return elementsCollectPhone(step.elements);
}

function showsInOptInCountry(form: SpotlightForm): boolean {
  const codes = form.geoTargetingCountryCodes.map((c) =>
    c.trim().toUpperCase(),
  );
  switch (form.geoTargetingType) {
    case SpotlightGeoTargetingType.ALL:
      return true;
    case SpotlightGeoTargetingType.INCLUDE:
      return codes.includes(OPT_IN_COUNTRY);
    case SpotlightGeoTargetingType.EXCLUDE:
      return !codes.includes(OPT_IN_COUNTRY);
  }
}

/**
 * Enabled popups that ask for a phone number. Embeds only render inside the
 * merchant's own markup, and Back in Stock popups ask for product alerts.
 */
function isLivePhonePopup(spotlight: SpotlightDocument): boolean {
  return (
    spotlight.enabled &&
    !spotlight.form.embed.enabled &&
    getSpotlightTriggerType(spotlight.form.triggers) !==
      SpotlightTriggerType.BACK_IN_STOCK &&
    showsInOptInCountry(spotlight.form) &&
    spotlight.form.steps.some(stepCollectsPhone)
  );
}

function viewportOf(spotlight: SpotlightDocument): RcsOptInViewport {
  return spotlight.form.deviceVisibility ===
    SpotlightDeviceVisibility.MOBILE_ONLY
    ? "mobile"
    : "desktop";
}

/** Desktop fits the whole disclosure in one legible frame, then recency. */
function byEvidenceQuality(a: SpotlightDocument, b: SpotlightDocument): number {
  return (
    Number(viewportOf(b) === "desktop") - Number(viewportOf(a) === "desktop") ||
    (b.publishedAt?.getTime() ?? 0) - (a.publishedAt?.getTime() ?? 0)
  );
}

function pickLivePhonePopup(
  spotlights: readonly SpotlightDocument[],
): SpotlightDocument | undefined {
  return spotlights.filter(isLivePhonePopup).sort(byEvidenceQuality)[0];
}

/** A spotlight `?spotlight-id=` will open on the storefront with a phone step. */
export function pickRcsOptInSpotlight(
  spotlights: readonly SpotlightDocument[],
): RcsOptInSpotlight | undefined {
  const chosen = pickLivePhonePopup(spotlights);
  return chosen
    ? { spotlightId: chosen._id.toString(), viewport: viewportOf(chosen) }
    : undefined;
}

interface RcsOptInRenderSpotlight {
  readonly spotlight: SpotlightForm;
  readonly phoneStepIndex: number;
  readonly viewport: RcsOptInViewport;
}

/** The live popup to draw at its phone step. */
export function pickRcsOptInRenderSpotlight(
  spotlights: readonly SpotlightDocument[],
): RcsOptInRenderSpotlight | undefined {
  const chosen = pickLivePhonePopup(spotlights);
  return chosen
    ? {
        spotlight: chosen.form,
        phoneStepIndex: chosen.form.steps.findIndex(stepCollectsPhone),
        viewport: viewportOf(chosen),
      }
    : undefined;
}

/** What a client needs to draw the storefront popup exactly as shoppers see it. */
export const rcsOptInRenderSchema = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("ready"),
    spotlight: SpotlightFormSchema,
    phoneStepIndex: z.number().int().nonnegative(),
    viewport: z.enum(["desktop", "mobile"]),
    brandKit: brandKitValidator,
    merchantName: z.string().optional(),
    supportEmail: z.string().optional(),
  }),
  z.object({ status: z.literal("blocked"), detail: z.string() }),
]);
export type RcsOptInRender = z.infer<typeof rcsOptInRenderSchema>;
